import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import type { InventoryMovement } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';

import { MovementKindBadge } from './stock-badges';

const signed = (quantity: number) => (quantity > 0 ? `+${quantity}` : String(quantity));

export interface MovementListProps {
  storeId: string;
  movements: readonly InventoryMovement[];
  /** Location names by id; given when the list spans locations, so each row names its own. */
  locationNames?: ReadonlyMap<string, string>;
}

/** Ledger rows, newest first: who changed what, why, and the order behind it. */
export function MovementList({ storeId, movements, locationNames }: MovementListProps) {
  const { t } = useTranslation('inventory');
  const format = useFormat();
  return (
    <ol aria-label={t('movements.title')} className="flex flex-col divide-y">
      {movements.map((movement) => {
        const actor =
          movement.actorName ??
          t(`movements.actor.${movement.actorType}`, { defaultValue: movement.actorType });
        const reference = movement.referenceType
          ? t(`movements.reference.${movement.referenceType}`, {
              defaultValue: movement.referenceType,
            })
          : null;
        return (
          <li key={movement.id} className="flex flex-col gap-1 py-3 text-sm">
            <div className="flex items-center justify-between gap-2">
              <span className="flex min-w-0 items-center gap-2">
                <MovementKindBadge kind={movement.kind} quantity={movement.quantity} />
                {locationNames ? (
                  <span className="text-muted-foreground truncate text-xs">
                    {locationNames.get(movement.locationId) ?? '—'}
                  </span>
                ) : null}
              </span>
              <span className="font-medium tabular-nums">{signed(movement.quantity)}</span>
            </div>
            <div className="text-muted-foreground flex flex-wrap justify-between gap-x-3 text-xs">
              <span>
                {t('movements.after', {
                  onHand: movement.onHandAfter,
                  reserved: movement.reservedAfter,
                })}
              </span>
              <span title={format.utc(movement.createdAt)}>
                {t('movements.by', { actor, when: format.relative(movement.createdAt) })}
              </span>
            </div>
            {movement.reason ? <p>{movement.reason}</p> : null}
            {reference ? (
              <p className="text-muted-foreground flex flex-wrap gap-x-2 text-xs">
                <span>{reference}</span>
                {movement.orderId ? (
                  <Link
                    to="/s/$storeId/orders/$orderId"
                    params={{ storeId, orderId: movement.orderId }}
                    className="text-foreground underline-offset-4 hover:underline"
                  >
                    {t('movements.order', { name: movement.orderName ?? '' })}
                  </Link>
                ) : null}
              </p>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}
